/**
 * RRGChart — Relative Rotation Graph quadrant chart (Iter224, visual rework Iter225).
 * Custom SVG (recharts couldn't do per-segment trail fading/smoothing).
 * X: RS-Ratio (relative strength vs SPY, 100 = benchmark). Y: RS-Momentum (4-wk).
 * Visual spec: smoothed Catmull-Rom trails fading toward now, dominant glowing
 * current dot with on-dot ticker, hover info strip, click-to-isolate.
 */
import { useMemo, useState } from "react";

export const QUADRANT_COLORS = {
  leading: "#10B981",
  weakening: "#F59E0B",
  lagging: "#EF4444",
  improving: "#3B82F6",
};

const quadrantOf = (x, y) =>
  x >= 100 ? (y >= 100 ? "leading" : "weakening") : (y >= 100 ? "improving" : "lagging");

const W = 860, H = 380;
const M = { l: 42, r: 16, t: 10, b: 32 };

// Cardinal spline (gentle tension) → cubic bezier for segment p1→p2.
// Divisor 14 (vs classic 6) prevents overshoot loops on zigzag trails.
const crSegment = (p0, p1, p2, p3) =>
  `M ${p1.px} ${p1.py} C ${p1.px + (p2.px - p0.px) / 14} ${p1.py + (p2.py - p0.py) / 14}, ` +
  `${p2.px - (p3.px - p1.px) / 14} ${p2.py - (p3.py - p1.py) / 14}, ${p2.px} ${p2.py}`;

export const RRGChart = ({ sectors, focus, onFocus }) => {
  const [hover, setHover] = useState(null);

  const { series, xTicks, yTicks, sx, sy } = useMemo(() => {
    const list = Object.entries(sectors || {})
      .filter(([, v]) => Array.isArray(v.trail) && v.trail.length >= 2)
      .map(([sector, v]) => ({ sector, etf: v.etf, trail: v.trail, cur: v.trail[v.trail.length - 1] }));
    const xs = list.flatMap(s => s.trail.map(p => p.x));
    const ys = list.flatMap(s => s.trail.map(p => p.y));
    // Proportional padding so edge dots (+glow) never clip on wide domains.
    const xr = Math.max(...xs, 100.6) - Math.min(...xs, 99.4);
    const yr = Math.max(...ys, 100.6) - Math.min(...ys, 99.4);
    const xPad = Math.max(0.6, xr * 0.045), yPad = Math.max(0.6, yr * 0.06);
    const x0 = Math.min(...xs, 99.4) - xPad, x1 = Math.max(...xs, 100.6) + xPad;
    const y0 = Math.min(...ys, 99.4) - yPad, y1 = Math.max(...ys, 100.6) + yPad;
    const sxF = (x) => M.l + ((x - x0) / (x1 - x0)) * (W - M.l - M.r);
    const syF = (y) => H - M.b - ((y - y0) / (y1 - y0)) * (H - M.t - M.b);
    const tick = (a, b, n) => Array.from({ length: n }, (_, i) => +(a + (i * (b - a)) / (n - 1)).toFixed(1));
    const withPx = list.map(s => ({
      ...s,
      pts: s.trail.map(p => ({ ...p, px: sxF(p.x), py: syF(p.y) })),
      color: QUADRANT_COLORS[quadrantOf(s.cur.x, s.cur.y)],
    }));
    return { series: withPx, xTicks: tick(x0, x1, 6), yTicks: tick(y0, y1, 5), sx: sxF, sy: syF };
  }, [sectors]);

  if (!series.length) return null;

  const cx100 = sx(100), cy100 = sy(100);
  const active = hover || focus;
  const dimmed = (name) => focus && focus !== name;

  return (
    <div className="relative" data-testid="rrg-chart">
      {/* hover/focus info strip */}
      {active && (() => {
        const s = series.find(x => x.sector === active);
        if (!s) return null;
        const q = quadrantOf(s.cur.x, s.cur.y);
        return (
          <div className="absolute top-1 right-2 z-10 border rounded-sm px-2 py-1 text-[10px] pointer-events-none"
            style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
            data-testid="rrg-info-strip">
            <span className="font-bold" style={{ color: "var(--hoip-text-primary)" }}>{s.sector}</span>
            <span className="font-mono-data text-[9px]"> ({s.etf})</span>
            {" · "}RS-Ratio <span className="font-mono-data">{s.cur.x}</span>
            {" · "}RS-Mom <span className="font-mono-data">{s.cur.y}</span>
            {" · "}<span className="font-bold uppercase text-[9px]" style={{ color: QUADRANT_COLORS[q] }}>{q}</span>
          </div>
        );
      })()}

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ cursor: focus ? "pointer" : "default" }}
        onClick={() => onFocus && onFocus(null)}>
        {/* quadrant fills */}
        <rect x={cx100} y={M.t} width={W - M.r - cx100} height={cy100 - M.t} fill="#10B981" opacity={0.04} />
        <rect x={cx100} y={cy100} width={W - M.r - cx100} height={H - M.b - cy100} fill="#F59E0B" opacity={0.04} />
        <rect x={M.l} y={cy100} width={cx100 - M.l} height={H - M.b - cy100} fill="#EF4444" opacity={0.04} />
        <rect x={M.l} y={M.t} width={cx100 - M.l} height={cy100 - M.t} fill="#3B82F6" opacity={0.04} />
        <text x={W - M.r - 6} y={M.t + 12} textAnchor="end" fontSize={9} fontWeight={700} fill="#10B981" opacity={0.5}>LEADING</text>
        <text x={W - M.r - 6} y={H - M.b - 6} textAnchor="end" fontSize={9} fontWeight={700} fill="#F59E0B" opacity={0.5}>WEAKENING</text>
        <text x={M.l + 6} y={H - M.b - 6} fontSize={9} fontWeight={700} fill="#EF4444" opacity={0.5}>LAGGING</text>
        <text x={M.l + 6} y={M.t + 12} fontSize={9} fontWeight={700} fill="#3B82F6" opacity={0.5}>IMPROVING</text>

        {/* center reference lines */}
        <line x1={cx100} y1={M.t} x2={cx100} y2={H - M.b} stroke="var(--hoip-border)" strokeWidth={1} />
        <line x1={M.l} y1={cy100} x2={W - M.r} y2={cy100} stroke="var(--hoip-border)" strokeWidth={1} />

        {/* axes */}
        {xTicks.map((t, i) => (
          <text key={`x${i}`} x={sx(t)} y={H - M.b + 12} textAnchor="middle" fontSize={8}
            fill="var(--hoip-text-tertiary)" fontFamily="'JetBrains Mono', monospace">{t}</text>
        ))}
        {yTicks.map((t, i) => (
          <text key={`y${i}`} x={M.l - 5} y={sy(t) + 3} textAnchor="end" fontSize={8}
            fill="var(--hoip-text-tertiary)" fontFamily="'JetBrains Mono', monospace">{t}</text>
        ))}
        <text x={(M.l + W - M.r) / 2} y={H - 4} textAnchor="middle" fontSize={9} fill="var(--hoip-text-tertiary)">
          RS-Ratio (relative strength vs SPY)
        </text>
        <text x={11} y={(M.t + H - M.b) / 2} textAnchor="middle" fontSize={9} fill="var(--hoip-text-tertiary)"
          transform={`rotate(-90 11 ${(M.t + H - M.b) / 2})`}>
          RS-Momentum (4-wk)
        </text>

        {/* trails (dimmed sectors first so focused draws on top) */}
        {[...series].sort((a, b) => (dimmed(a.sector) ? -1 : 1) - (dimmed(b.sector) ? -1 : 1)).map(s => {
          const dim = dimmed(s.sector);
          const stroke = dim ? "#71717A" : s.color;
          const N = s.pts.length;
          return (
            <g key={s.sector} opacity={dim ? 0.18 : 1}>
              {/* smoothed fading trail segments */}
              {s.pts.slice(0, -1).map((p, i) => {
                const p0 = s.pts[Math.max(0, i - 1)], p1 = s.pts[i], p2 = s.pts[i + 1], p3 = s.pts[Math.min(N - 1, i + 2)];
                const op = 0.15 + 0.75 * ((i + 1) / (N - 1));
                return <path key={i} d={crSegment(p0, p1, p2, p3)} fill="none" stroke={stroke}
                  strokeWidth={1} strokeOpacity={dim ? 0.6 : op} strokeLinecap="round" />;
              })}
              {/* fading trail points */}
              {s.pts.slice(0, -1).map((p, i) => (
                <circle key={`d${i}`} cx={p.px} cy={p.py} r={1.6} fill={stroke}
                  opacity={dim ? 0.5 : 0.15 + 0.75 * ((i + 1) / (N - 1))} />
              ))}
              {/* dominant current dot: glow + core + on-dot ticker */}
              {(() => {
                const c = s.pts[N - 1];
                return (
                  <g style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover(s.sector)} onMouseLeave={() => setHover(null)}
                    onClick={(e) => { e.stopPropagation(); onFocus && onFocus(focus === s.sector ? null : s.sector); }}
                    data-testid={`rrg-dot-${s.etf}`}>
                    {!dim && <circle cx={c.px} cy={c.py} r={13} fill={s.color} opacity={0.18} />}
                    <circle cx={c.px} cy={c.py} r={8} fill={dim ? "#71717A" : s.color}
                      stroke="#0a0a0a" strokeWidth={1.2} />
                    <text x={c.px} y={c.py + 2.2} textAnchor="middle"
                      fontSize={s.etf.length > 4 ? 3.9 : s.etf.length > 3 ? 4.6 : 5.6}
                      fontWeight={800} fill="#0a0a0a" fontFamily="'JetBrains Mono', monospace"
                      style={{ pointerEvents: "none" }}>{s.etf}</text>
                  </g>
                );
              })()}
            </g>
          );
        })}
      </svg>

      <div className="flex items-center gap-3 flex-wrap px-2 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        {Object.entries(QUADRANT_COLORS).map(([q, c]) => (
          <span key={q} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: c }} />
            {q === "leading" ? "Leading — strong & accelerating" :
             q === "weakening" ? "Weakening — strong but fading" :
             q === "lagging" ? "Lagging — weak & fading" : "Improving — weak but accelerating"}
          </span>
        ))}
        <span className="italic">Trail = ~8 weekly points fading toward now · click a dot or table row to isolate, click again to reset</span>
      </div>
    </div>
  );
};
